/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Tabs } from 'antd-mobile'
import { PAGE_TYPE, SPORTS_TYPE } from '@interface/common'
import { FavoriteIco } from '@shared/Icon/MatchupIcon'
import {
  GAME_TYPE,
  MAJOR_SPORT_IDS,
  UNIFIED_REGULAR_ODDS_2_WAY,
  UNIFIED_REGULAR_ODDS_HANDICAP,
  UNIFIED_REGULAR_ODDS_UNDEROVER,
  UNIFIED_REGULAR_ODDS_WAY,
} from '@game-type/game-market-types'
import useRootStore from '@store/useRootStore'
import * as _ from 'lodash'
import { Models } from 'rmc-tabs/lib/Models'
import { observer } from 'mobx-react'
import { EXCEPTIONAL_UNIQUE_ID } from '@v2/constants/common'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import React, { useEffect, useMemo } from 'react'
import { Schema$UserPickMarket } from '@interface/user-pick'
import { SPORT_ID } from 'adv-protocol/src/GameCommon'
import { isMajorSportsType } from '@utils/utils'
import FavoriteActiveImg from '@assets/images/btn/favorite-manage-active.png'
import { SelectedLeague } from '@views/Matchup/MatchField/MatchTab/index.style'
import { Button, CloseBtn } from '@styles/button.style'

const FavoriteActiveBtn = <img src={FavoriteActiveImg} className="favorite-btn-active-img" alt="favorite-btn-active"/>

interface LeagueTabData extends Models.TabData {
  sportId?: number
}

function makeFavoriteFilter(favoriteMatchCount: number): LeagueTabData {
  return {
    title: (
      <div className="etc-league tab-item favorite-tab">
        <span className="favorite-effect"/>
        <span className="txt favorite">{FavoriteActiveBtn}<FavoriteIco/></span>
        <span className="num">{favoriteMatchCount}</span>
      </div>
    ),
    name: '찜한경기',
    key: EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH.toString(),
    sportId: EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH,
  }
}

function makeMajorFilter(recommendMatchCount: number): LeagueTabData {
  return {
    title: (
      <div className="tab-item">
        <span className="txt">주요</span>
        <span className="num">{recommendMatchCount}</span>
      </div>
    ),
    name: '주요리그',
    key: EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE.toString(),
    sportId: EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE,
  }
}

function makeAllFilter(totalMatchCount: number): LeagueTabData {
  return {
    title: (
      <div className="etc-league tab-item">
        <span className="txt">전체</span>
        <span className="num">{totalMatchCount}</span>
      </div>
    ),
    name: '전체리그',
    key: EXCEPTIONAL_UNIQUE_ID.TOTAL_LEAGUE.toString(),
    sportId: EXCEPTIONAL_UNIQUE_ID.TOTAL_LEAGUE,
  }
}

function makeLiveFilter(totalMatchCount: number): LeagueTabData {
  return {
    title: (
      <div className="etc-league tab-item">
        <span className="txt">진행중</span>
        <span className="num">{totalMatchCount}</span>
      </div>
    ),
    name: '진행중리그',
    key: EXCEPTIONAL_UNIQUE_ID.PROGRESSING_MATCH.toString(),
    sportId: EXCEPTIONAL_UNIQUE_ID.PROGRESSING_MATCH,
  }
}

function makeMybetFilter(myBetCount: number): LeagueTabData {
  return {
    title: (
      <div className="etc-league tab-item">
        <span className="txt">마이벳</span>
        <span className="num">{myBetCount}</span>
      </div>
    ),
    name: '마이베팅리그',
    key: EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH.toString(),
    sportId: EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH,
  }
}

function makeUserPickFilter(params: {
  notStartedMatchCount: number
  startedMatchCount: number
  closedMatchCount: number
  favoriteMatchCount: number
}): LeagueTabData[] {
  const { notStartedMatchCount, startedMatchCount, closedMatchCount, favoriteMatchCount } = params
  return [
    {
      title: (
        <div className="tab-item">
          <span className="txt">시작전</span>
          <span className="num">{notStartedMatchCount}</span>
        </div>
      ),
      name: '시작전',
      key: EXCEPTIONAL_UNIQUE_ID.NOT_STARTED_USER_PICK.toString(),
    },
    {
      title: (
        <div className="tab-item">
          <span className="txt">진행중</span>
          <span className="num">{startedMatchCount}</span>
        </div>
      ),
      name: '진행중',
      key: EXCEPTIONAL_UNIQUE_ID.STARTED_USER_PICK.toString(),
    },
    {
      title: (
        <div className="tab-item">
          <span className="txt">종료</span>
          <span className="num">{closedMatchCount}</span>
        </div>
      ),
      name: '종료',
      key: EXCEPTIONAL_UNIQUE_ID.CLOSED_USER_PICK.toString(),
    },
    {
      title: (
        <div className="etc-league tab-item favorite-tab">
          <span className="favorite-effect"/>
          <span className="txt favorite">{FavoriteActiveBtn}<FavoriteIco/></span>
          <span className="num">{favoriteMatchCount}</span>
        </div>
      ),
      name: '찜한경기',
      key: EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH.toString(),
    },
  ]
}

function getMarketCount(userPickMarkets: Schema$UserPickMarket[]): Record<number, number> {
  let wayCount = 0
  let uoCount = 0
  let hcCount = 0
  let etcCount = 0
  _.forEach(userPickMarkets, market => {
    const { marketType } = market
    if (UNIFIED_REGULAR_ODDS_WAY.includes(marketType.toString())) {
      wayCount += market.userPickCount
    } else if (UNIFIED_REGULAR_ODDS_UNDEROVER.includes(marketType.toString())) {
      uoCount += market.userPickCount
    } else if (UNIFIED_REGULAR_ODDS_HANDICAP.includes(marketType.toString())) {
      hcCount += market.userPickCount
    } else {
      etcCount += market.userPickCount
    }
  })
  return {
    [EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK]: wayCount + uoCount + hcCount + etcCount,
    [EXCEPTIONAL_UNIQUE_ID.WAY_USER_PICK]: wayCount,
    [EXCEPTIONAL_UNIQUE_ID.UO_USER_PICK]: uoCount,
    [EXCEPTIONAL_UNIQUE_ID.HANDI_USER_PICK]: hcCount,
    [EXCEPTIONAL_UNIQUE_ID.ETC_USER_PICK]: etcCount,
  }
}

function isTwoWayMarket(userPickMarkets: Schema$UserPickMarket[], currentSportsKey: string): boolean {
  const exists = _.find(userPickMarkets, m => {
    return UNIFIED_REGULAR_ODDS_2_WAY.includes(_.toString(m.marketType))
  })
  if (_.isNil(exists) && !_.isNil(currentSportsKey)) {
    if (Number(currentSportsKey) === SPORT_ID.SOCCER || Number(currentSportsKey) === SPORT_ID.ICE_HOCKEY) {
      return false
    }
    if (Number(currentSportsKey) === SPORT_ID.BASKETBALL
      || Number(currentSportsKey) === SPORT_ID.BASEBALL
      || Number(currentSportsKey) === SPORT_ID.VOLLEYBALL
    ) {
      return true
    }
    return false
  }
  return !_.isNil(exists)
}

function makeUserPickDetailFilter(userPickMarkets: Schema$UserPickMarket[], currentSportsKey: string): LeagueTabData[] {
  const countMap = getMarketCount(userPickMarkets)
  const isTwoWay = isTwoWayMarket(userPickMarkets, currentSportsKey)
  return [
    {
      title: (
        <div className="tab-item">
          <span className="txt">전체</span>
          <span className="num">{countMap[EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK]}</span>
        </div>
      ),
      name: '전체',
      key: EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK.toString(),
    },
    {
      title: (
        <div className="tab-item">
          <span className="txt">{isTwoWay ? '승패' : '승무패'}</span>
          <span className="num">{countMap[EXCEPTIONAL_UNIQUE_ID.WAY_USER_PICK]}</span>
        </div>
      ),
      name: `${isTwoWay ? '승패' : '승무패'}`,
      key: EXCEPTIONAL_UNIQUE_ID.WAY_USER_PICK.toString(),
    },
    {
      title: (
        <div className="tab-item">
          <span className="txt">언더오버</span>
          <span className="num">{countMap[EXCEPTIONAL_UNIQUE_ID.UO_USER_PICK]}</span>
        </div>
      ),
      name: '언더오버',
      key: EXCEPTIONAL_UNIQUE_ID.UO_USER_PICK.toString(),
    },
    {
      title: (
        <div className="tab-item">
          <span className="txt">핸디캡</span>
          <span className="num">{countMap[EXCEPTIONAL_UNIQUE_ID.HANDI_USER_PICK]}</span>
        </div>
      ),
      name: '핸디캡',
      key: EXCEPTIONAL_UNIQUE_ID.HANDI_USER_PICK.toString(),
    },
    {
      title: (
        <div className="tab-item">
          <span className="txt">기타</span>
          <span className="num">{countMap[EXCEPTIONAL_UNIQUE_ID.ETC_USER_PICK]}</span>
        </div>
      ),
      name: '기타',
      key: EXCEPTIONAL_UNIQUE_ID.ETC_USER_PICK.toString(),
    },
  ]
}

export interface LeagueTabProps {
  pageType?: PAGE_TYPE
}

export const LeagueTab: React.FC<LeagueTabProps> = observer((props) => {
  const { matchupStore, authStore, userPickStore, componentStore, logStore } = useRootStore()
  const {
    currentSportsKey,
    favoriteMatchCount,
    totalMatchCount,
    totalLiveMatchCount,
    currentGameKey,
    recommendMatchCount,
    progressingMatchCount,
    majorUniqueID,
    matchTabInitializeMajorUniqueId,
    myBetMatchCount,
    leagueTabHotMatchCount,
  } = matchupStore
  const {
    notStartedMatchCount,
    startedMatchCount,
    closedMatchCount,
    favoriteMatchIds,
    userPickMarketFilter,
    userPickMarkets,
  } = userPickStore
  const { pageType } = props

  const isResultPage = pageType === PAGE_TYPE.MATCHUP_RESULT

  const FilterTabs: LeagueTabData[] = []
  // currentSportsKey= 0: 기타, 0 >: 해당 스포츠
  if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
    if (userPickMarkets) {
      if (userPickStore.activeUserFilter && !_.isEmpty(userPickStore.userPicks)) {
        const nickName = userPickStore.userPicks[0]?.pickOwner?.userNick
        const filters = makeUserPickUserFilter(nickName)
        FilterTabs.push(filters)
      } else {
        const filters = makeUserPickDetailFilter(userPickMarkets, currentSportsKey)
        for (const filter of filters) {
          FilterTabs.push(filter)
        }
      }
    }
  } else if (pageType === PAGE_TYPE.USER_PICK) {
    const favoriteMatchCount = _.size(favoriteMatchIds)
    const filters = makeUserPickFilter({
      notStartedMatchCount, startedMatchCount, closedMatchCount, favoriteMatchCount,
    })
    for (const filter of filters) {
      FilterTabs.push(filter)
    }
  } else if (currentSportsKey === SPORTS_TYPE.TODAY && !isResultPage) {
    if (currentGameKey !== GAME_TYPE.LIVE) {
      // 스포츠 - 전체 - 전체
      FilterTabs.push(makeAllFilter(totalMatchCount))
      // 스포츠 - 전체 - 주요
      FilterTabs.push(makeMajorFilter(recommendMatchCount))
      // 스포츠 - 전체 - 찜
      FilterTabs.push(makeFavoriteFilter(favoriteMatchCount))
    } else {
      // 라이브 - 전체 - 전체
      FilterTabs.push(makeAllFilter(totalLiveMatchCount))
      // 라이브 - 5대 종목 - 진행중
      FilterTabs.push(makeLiveFilter(progressingMatchCount))
      // 라이브 - 전체 - 마이베팅
      FilterTabs.push(makeMybetFilter(myBetMatchCount))
      // 라이브 - 전체 - 찜
      FilterTabs.push(makeFavoriteFilter(favoriteMatchCount))
    }
  } else if (currentGameKey !== GAME_TYPE.LIVE) {
    // 스포츠 - 종목 - 전체
    const allCount = matchupStore.currentSportsKey === SPORTS_TYPE.HOT ? leagueTabHotMatchCount : totalMatchCount
    FilterTabs.push(makeAllFilter(allCount))
    // 스포츠 - 종목 - 주요 (주요 탭은 전체 종목, 5대 종목에서만 표시한다)
    if (isMajorSportsType(currentSportsKey)) {
      FilterTabs.push(makeMajorFilter(recommendMatchCount))
    }
    if (currentSportsKey === SPORTS_TYPE.TODAY && isResultPage){
      // 경기결과 - 전체 - 마이베팅
      FilterTabs.push(makeMybetFilter(myBetMatchCount))
    }
    // 스포츠 - 종목 - 찜
    FilterTabs.push(makeFavoriteFilter(favoriteMatchCount))
  } else {
    // 라이브 - 5대 종목 - 전체
    const allCount = matchupStore.currentSportsKey === SPORTS_TYPE.HOT ? leagueTabHotMatchCount : totalMatchCount
    FilterTabs.push(makeAllFilter(allCount))
    // 라이브 - 5대 종목 - 진행중
    FilterTabs.push(makeLiveFilter(progressingMatchCount))
    // 라이브 - 5대 종목 - 찜
    FilterTabs.push(makeFavoriteFilter(favoriteMatchCount))
  }

  const idx = useMemo(() => {
    if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
      return _.findIndex(FilterTabs, { key: _.toString(userPickMarketFilter) })
    } else if (!_.isNil(majorUniqueID)) {
      return _.findIndex(FilterTabs, { key: _.toString(majorUniqueID) })
    } else if (isResultPage
      && (MAJOR_SPORT_IDS.includes(Number(currentSportsKey)) || currentSportsKey === SPORTS_TYPE.TODAY)) {
      return _.findIndex(FilterTabs, { key: _.toString(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE) })
    } else {
      return 0
    }
  }, [pageType, userPickMarketFilter, majorUniqueID, currentSportsKey])

  const onTabMajorChanged = (td: Models.TabData) => {
    switch (pageType) {
      case PAGE_TYPE.MATCHUP: {
        if (currentGameKey === GAME_TYPE.LIVE) {
          if (td.key === EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH.toString()) {
            logStore.writeButtonLog({ pageId: 'b020', buttonId: '002' }).catch(console.error)
          } else if (td.key === EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH.toString()) {
            logStore.writeButtonLog({ pageId: 'b020', buttonId: '003' }).catch(console.error)
          }
        } else if (currentGameKey === GAME_TYPE.CROSS) {
          if (td.key === EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH.toString()) {
            logStore.writeButtonLog({ pageId: 'b010', buttonId: '004' }).catch(console.error)
          }
        }
        break
      }
      case PAGE_TYPE.MATCHUP_RESULT: {
        if (td.key === EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH.toString()) {
            logStore.writeButtonLog({ pageId: 'b030', buttonId: '002' }).catch(console.error)
          }
        break
      }
    }

    if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
      userPickStore.setUserPickMarketFilter(Number(td.key))
    } else {
      if (pageType === PAGE_TYPE.USER_PICK) {
        matchupStore.setMatchTabInitializeMajorUniqueId(null)
      }
      matchupStore.setMajorUniqueID(Number(td.key))
      componentStore.setIsGoToScrollTopOfMatchList(true)

      if (currentSportsKey === SPORTS_TYPE.ETC && !_.isNil(td.sportId)) {
        matchupStore.setSportId(td.sportId)
      }
    }

    authStore.checkAdultAuthentication().catch(console.error)
  }

  const initializeAllTabState = (isUnMount: boolean): void => {
    if (isUnMount) {
      if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
        userPickStore.setUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK)
      } else {
        matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE)
      }
    } else if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
      userPickStore.setUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK)
    } else if (currentGameKey === GAME_TYPE.LIVE) {
      matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.TOTAL_LEAGUE)
    } else if (pageType === PAGE_TYPE.MATCHUP_RESULT
      && (MAJOR_SPORT_IDS.includes(Number(currentSportsKey)) || currentSportsKey === SPORTS_TYPE.TODAY)) {
      matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE)
    } else if (pageType !== PAGE_TYPE.MATCHUP && !_.isNil(matchTabInitializeMajorUniqueId)) {
      matchupStore.setMajorUniqueID(matchTabInitializeMajorUniqueId)
    } else if (pageType === PAGE_TYPE.MATCHUP
      && matchTabInitializeMajorUniqueId === EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH) {
      matchupStore.setMajorUniqueID(matchTabInitializeMajorUniqueId)
    } else if (currentSportsKey === SPORTS_TYPE.TODAY) {
      matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE)
    } else if (!_.isNil(currentGameKey) && !_.isNil(currentSportsKey)) {
      matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.TOTAL_LEAGUE)
    } else {
      matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE)
    }
  }

  useEffect(() => {
    if (!_.isNil(currentGameKey) && !_.isNil(currentSportsKey)) {
      if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
        if (userPickStore.activeUserFilter) {
          userPickStore.setUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK)
        } else {
          userPickStore.setUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.WAY_USER_PICK)
        }
      } else if (!_.isNil(matchTabInitializeMajorUniqueId)) {
        matchupStore.setMajorUniqueID(matchTabInitializeMajorUniqueId)
      } else if (pageType === PAGE_TYPE.USER_PICK) {
        matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.NOT_STARTED_USER_PICK)
      } else if (currentGameKey !== GAME_TYPE.LIVE && currentSportsKey === SPORTS_TYPE.TODAY) {
        matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE)
      } else if (currentGameKey === GAME_TYPE.LIVE) {
        matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MINOR_LEAGUE)
      } else if (pageType === PAGE_TYPE.MATCHUP_RESULT
        && (MAJOR_SPORT_IDS.includes(Number(currentSportsKey)) || currentSportsKey === SPORTS_TYPE.TODAY)) {
        matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE)
      } else {
        matchupStore.setMajorUniqueID(EXCEPTIONAL_UNIQUE_ID.TOTAL_LEAGUE)
      }
    }
  }, [currentGameKey, currentSportsKey])

  const onClickDeactivateUserFilter = () => {
    userPickStore.setActiveUserFilter(false)
  }

  function makeUserPickUserFilter(nick: string): LeagueTabData {
    return {
      title: (
        <SelectedLeague className="user-pick-filter">
        <span className="quick-selected-league-name">
           {nick}
        </span>
          <Button css={CloseBtn} className="quick-selected-league-btn" onClick={() => {
            onClickDeactivateUserFilter()
          }}>
            <span className="close-txt">닫기</span>
            <span className="close-btn"/>
          </Button>
        </SelectedLeague>
      ),
      name: '유저필터',
      key: EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK.toString(),
    }
  }

  useOnMount(() => {
    initializeAllTabState(false)
  })

  useOnUnmount(() => {
    initializeAllTabState(true)
  })

  return (
    <Tabs
      tabs={FilterTabs}
      page={idx}
      onTabClick={onTabMajorChanged}
      prefixCls="am-tabs"
      renderTabBar={tabProps => {
        return <Tabs.DefaultTabBar {...tabProps} page={FilterTabs.length}/>
      }}
    />
  )
})
